import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, LogOut, Languages, Wifi, WifiOff, Search, KeyRound } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useAuth, ROLE_LABEL } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { syncLocal } from "../lib/offline";
import { toast } from "sonner";

function NotifBell() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [d, setD] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="nav-notifications-bell" className="relative h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center" aria-label={t("notifications")}>
          <Bell className="h-5 w-5" />
          {d.unread > 0 && <span data-testid="nav-notifications-count" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#D4AF37] text-[#2F2A24] text-xs font-bold inline-flex items-center justify-center">{d.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[92vw] max-w-sm p-0 bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E4DACD]">
          <span className="font-bold text-[#2F2A24]">{t("notifications")}</span>
          <button data-testid="notifications-mark-all-read" onClick={readAll} className="text-sm text-[#C85A32] font-semibold">{t("mark_all_read")}</button>
        </div>
        <div className="max-h-96 overflow-auto">
          {d.items.length === 0 && <p className="p-4 text-sm text-[#6E6259]">No notifications yet.</p>}
          {d.items.map((n) => (
            <button key={n.id} onClick={() => nav(n.link || "/")} data-testid={`notification-item-${n.id}`} className={`block w-full text-left px-4 py-3 border-b border-[#F3EDE2] hover:bg-[#FAF7F2] ${n.read ? "" : "bg-[#FEF9E7]"}`}>
              <div className="font-semibold text-sm text-[#2F2A24]">{n.title}</div>
              <div className="text-xs text-[#6E6259] mt-0.5">{n.body}</div>
              <div className="text-[11px] text-[#9C8F84] mt-1">{n.created_at?.replace("T", " ").slice(0, 16)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Shell({ children }) {
  const { user, logout, offline } = useAuth();
  const { t, lang, toggle, nextLabel } = useI18n();
  useEffect(() => {
    if (!offline && user) {
      syncLocal(user.id).then((n) => n && toast.success(`${n} offline draft(s) uploaded as server drafts. Please review and submit them.`));
    }
  }, [offline, user]);
  return (
    <div className="min-h-screen bg-[#FAF7F2] grain">
      <header className="bg-[#5C4033] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Link to="/" data-testid="nav-home-link" className="flex items-center gap-3 min-w-0 flex-1">
            <img src="/parish-logo.png" alt="Parish seal" className="h-11 w-11 shrink-0 rounded-full object-cover bg-white" data-testid="header-logo" />
            <div className="min-w-0">
              <div className="font-serif font-bold text-sm sm:text-lg leading-tight truncate">St. Francis of Assisi Parish</div>
              <div className="text-[11px] sm:text-xs text-white/75 truncate">Brgy. Mabuhay, GenSan · Diocese of Marbel · {t("app_sub")}</div>
            </div>
          </Link>
          <span data-testid="nav-online-status" title={offline ? "Offline" : "Online"} className={`hidden sm:inline-flex h-8 items-center gap-1 rounded-full px-3 text-xs font-bold ${offline ? "bg-[#DC2626]" : "bg-white/10"}`}>
            {offline ? <WifiOff className="h-4 w-4" /> : <Wifi className="h-4 w-4" />} {offline ? "Offline" : "Online"}
          </span>
          <button data-testid="nav-language-toggle" onClick={toggle} className="h-11 rounded-full bg-[#D4AF37] text-[#2F2A24] px-3 sm:px-4 font-bold text-sm inline-flex items-center gap-1.5">
            <Languages className="h-4 w-4" /> {nextLabel}
          </button>
          {user && !offline && <Link to="/search" data-testid="nav-search-link" className="hidden sm:inline-flex h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 items-center justify-center" aria-label="Search"><Search className="h-5 w-5" /></Link>}
          {user && !offline && <NotifBell />}
          {user && !offline && <Link to="/account" data-testid="nav-change-password-link" title="My Account and security" className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center" aria-label="My Account and security"><KeyRound className="h-5 w-5" /></Link>}
          {user && (
            <button data-testid="nav-logout-button" onClick={logout} className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center" aria-label={t("logout")}>
              <LogOut className="h-5 w-5" />
            </button>
          )}
        </div>
        {user && (
          <div className="bg-[#4A3326] text-white/85 text-xs sm:text-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-1.5 flex justify-between gap-2">
              <span data-testid="nav-user-badge" className="truncate">{user.name} · {ROLE_LABEL[user.role]}</span>
              <span className="shrink-0">Philippine Standard Time</span>
            </div>
          </div>
        )}
      </header>
      {offline && <div data-testid="offline-banner" className="bg-[#FEF3C7] text-[#92400E] text-sm font-semibold px-4 py-3 text-center border-b border-[#FDE68A]">{t("offline_banner")}</div>}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
      <footer className="text-center text-xs text-[#6E6259] pb-8 px-4">
        Saint Francis of Assisi Parish · GKK-FMIS · Records marked “DEMO” are sample data, not actual parish financial records.
      </footer>
    </div>
  );
}
